/**
 * Google Analytics 4 — Conversion Funnel Tracking Service
 *
 * Funnel stages tracked:
 *   1. property_view          — user opens a property page
 *   2. alert_signup           — user creates / activates a property alert
 *   3. notification_received  — email / SMS notification dispatched
 *   4. appointment_booked     — user successfully books a visit
 *
 * All helpers are silent-fail: GA errors never break the app.
 */

const GA_ID = import.meta.env?.VITE_GA_MEASUREMENT_ID;

// ─── Internal helper ─────────────────────────────────────────────────────────

const gtag = (...args) => {
  try {
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      window.gtag(...args);
    }
  } catch (_) {
    // silent fail
  }
};

// ─── Bootstrap (called once from useGoogleAnalytics hook) ────────────────────

export const initGA = () => {
  if (!GA_ID || typeof window === 'undefined') return;
  if (window.__ga_initialized) return;
  window.__ga_initialized = true;

  const script = document.createElement('script');
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  script.async = true;
  document.head?.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  window.gtag = function (...args) { window.dataLayer?.push(args); };
  window.gtag('js', new Date());
  window.gtag('config', GA_ID, { send_page_view: false });
};

// ─── Page view ───────────────────────────────────────────────────────────────

export const trackPageView = (path) => {
  if (!GA_ID) return;
  gtag('event', 'page_view', {
    page_path: path || window?.location?.pathname,
    send_to: GA_ID,
  });
};

// ─── Funnel Stage 1 — Property View ──────────────────────────────────────────

/**
 * Fire when a user lands on an individual property page.
 * @param {{ id, title, commune, type, price }} property
 */
export const gaTrackPropertyView = (property) => {
  if (!property?.id) return;
  gtag('event', 'property_view', {
    event_category: 'Conversion Funnel',
    event_label: property?.title || property?.id,
    property_id: property?.id,
    property_commune: property?.commune || property?.commune_name || '',
    property_type: property?.property_type || property?.type || '',
    property_price: property?.price || property?.promotional_price || 0,
    funnel_stage: 1,
    funnel_stage_name: 'property_view',
  });
};

// ─── Funnel Stage 2 — Alert Signup ───────────────────────────────────────────

/**
 * Fire when a user creates or activates a property alert.
 * @param {{ name, commune_names, property_types, notify_email, notify_sms }} alertData
 */
export const gaTrackAlertSignup = (alertData = {}) => {
  gtag('event', 'alert_signup', {
    event_category: 'Conversion Funnel',
    event_label: alertData?.name || 'property_alert',
    alert_communes: (alertData?.commune_names || [])?.join(', '),
    alert_types: (alertData?.property_types || [])?.join(', '),
    notify_email: alertData?.notify_email ? 'yes' : 'no',
    notify_sms: alertData?.notify_sms ? 'yes' : 'no',
    funnel_stage: 2,
    funnel_stage_name: 'alert_signup',
  });
};

// ─── Funnel Stage 3 — Notification Received (Email / SMS) ────────────────────

/**
 * Fire when a notification (email or SMS) is dispatched to the user.
 * @param {{ channel: 'email'|'sms', type: string, propertyId?: string }} params
 */
export const gaTrackNotificationReceived = (params = {}) => {
  gtag('event', 'notification_received', {
    event_category: 'Conversion Funnel',
    event_label: params?.type || 'notification',
    notification_channel: params?.channel || 'email',
    notification_type: params?.type || '',
    property_id: params?.propertyId || '',
    funnel_stage: 3,
    funnel_stage_name: 'notification_received',
  });
};

// ─── Funnel Stage 4 — Appointment Booked ─────────────────────────────────────

/**
 * Fire when a user successfully books an appointment.
 * @param {{ id, title, commune, type, price }} property
 * @param {{ type: string, scheduledDate: string }} appointmentData
 */
export const gaTrackAppointmentBooked = (property = {}, appointmentData = {}) => {
  gtag('event', 'appointment_booked', {
    event_category: 'Conversion Funnel',
    event_label: property?.title || property?.id || 'appointment',
    property_id: property?.id || '',
    property_commune: property?.commune || property?.commune_name || '',
    property_type: property?.property_type || property?.type || '',
    property_price: property?.price || property?.promotional_price || 0,
    appointment_type: appointmentData?.type || '',
    scheduled_date: appointmentData?.scheduledDate || '',
    funnel_stage: 4,
    funnel_stage_name: 'appointment_booked',
    // GA4 conversion value — use property price as proxy
    value: property?.price || property?.promotional_price || 0,
    currency: 'XOF',
  });

  // Also fire the GA4 recommended "purchase" / conversion event so it
  // shows up automatically in the Conversions report
  gtag('event', 'generate_lead', {
    currency: 'XOF',
    value: property?.price || property?.promotional_price || 0,
    property_id: property?.id || '',
  });
};

// ─── Drop-off helper (optional — fires when user leaves funnel) ───────────────

/**
 * Fire when a user abandons a funnel step (e.g. closes scheduling form).
 * @param {number} stage  1-4
 * @param {string} reason optional label
 */
export const gaTrackFunnelDropOff = (stage, reason = '') => {
  gtag('event', 'funnel_drop_off', {
    event_category: 'Conversion Funnel',
    event_label: reason || `stage_${stage}`,
    abandoned_stage: stage,
  });
};

// ─── Property Search ──────────────────────────────────────────────────────────

/**
 * Fire when a user performs a property search (filters applied or query submitted).
 * @param {{ query?: string, commune?: string, propertyType?: string, minPrice?: number, maxPrice?: number, resultsCount?: number }} searchParams
 */
export const gaTrackPropertySearch = (searchParams = {}) => {
  gtag('event', 'property_search', {
    event_category: 'Discovery',
    event_label: searchParams?.query || searchParams?.commune || 'search',
    search_query: searchParams?.query || '',
    search_commune: searchParams?.commune || '',
    search_property_type: searchParams?.propertyType || '',
    search_min_price: searchParams?.minPrice || 0,
    search_max_price: searchParams?.maxPrice || 0,
    search_results_count: searchParams?.resultsCount ?? -1,
  });
};

// ─── Video View ───────────────────────────────────────────────────────────────

/**
 * Fire when a user starts playing a property video.
 * @param {{ id?: string, title?: string, commune?: string, type?: string }} property
 * @param {{ url?: string, duration?: number }} videoData
 */
export const gaTrackVideoView = (property = {}, videoData = {}) => {
  gtag('event', 'video_view', {
    event_category: 'Engagement',
    event_label: property?.title || property?.id || 'video',
    property_id: property?.id || '',
    property_commune: property?.commune || property?.commune_name || '',
    property_type: property?.property_type || property?.type || '',
    video_duration: videoData?.duration || 0,
    video_url: videoData?.url || '',
  });
};

// ─── ROI Calculation ──────────────────────────────────────────────────────────

/**
 * Fire when a user completes an ROI calculation.
 * @param {{ propertyPrice?: number, propertyId?: string, totalROI?: number, capRate?: number, cashOnCashReturn?: number }} calcData
 */
export const gaTrackROICalculation = (calcData = {}) => {
  gtag('event', 'roi_calculation', {
    event_category: 'Investment Tools',
    event_label: calcData?.propertyId || 'roi_calc',
    property_id: calcData?.propertyId || '',
    property_price: calcData?.propertyPrice || 0,
    roi_total: calcData?.totalROI || 0,
    roi_cap_rate: calcData?.capRate || 0,
    roi_cash_on_cash: calcData?.cashOnCashReturn || 0,
  });
};

// ─── Inquiry Creation ─────────────────────────────────────────────────────────

/**
 * Fire when a user successfully submits a property inquiry.
 * @param {{ id?: string, title?: string, commune?: string, type?: string, price?: number }} property
 * @param {{ preferredContactTime?: string }} inquiryData
 */
export const gaTrackInquiryCreated = (property = {}, inquiryData = {}) => {
  gtag('event', 'inquiry_created', {
    event_category: 'Conversion Funnel',
    event_label: property?.title || property?.id || 'inquiry',
    property_id: property?.id || '',
    property_commune: property?.commune || property?.commune_name || '',
    property_type: property?.property_type || property?.type || '',
    property_price: property?.price || property?.promotional_price || 0,
    preferred_contact_time: inquiryData?.preferredContactTime || '',
    funnel_stage: 5,
    funnel_stage_name: 'inquiry_created',
    value: property?.price || property?.promotional_price || 0,
    currency: 'XOF',
  });

  // Also fire GA4 generate_lead conversion event
  gtag('event', 'generate_lead', {
    currency: 'XOF',
    value: property?.price || property?.promotional_price || 0,
    property_id: property?.id || '',
    lead_source: 'inquiry_form',
  });
};

// ─── Matterport Tour Engagement ────────────────────────────────────────────────

/**
 * Fire when a user starts a Matterport immersive tour.
 * @param {{ tourTitle?: string, communeName?: string, tourIndex?: number, quality?: string }} params
 */
export const gaTrackMatterportTourStart = (params = {}) => {
  gtag('event', 'matterport_tour_start', {
    event_category: 'Matterport Engagement',
    event_label: params?.tourTitle || params?.communeName || 'tour',
    tour_title: params?.tourTitle || '',
    tour_commune: params?.communeName || '',
    tour_index: params?.tourIndex ?? 0,
    connection_quality: params?.quality || 'medium',
  });
};

/**
 * Fire when a user uses a swipe gesture (left/right) to navigate between tours.
 * @param {{ direction?: 'left'|'right', communeName?: string, fromIndex?: number, toIndex?: number }} params
 */
export const gaTrackMatterportSwipeGesture = (params = {}) => {
  gtag('event', 'matterport_swipe_gesture', {
    event_category: 'Matterport Engagement',
    event_label: `swipe_${params?.direction || 'unknown'}_${params?.communeName || ''}`,
    gesture_type: 'swipe',
    swipe_direction: params?.direction || '',
    tour_commune: params?.communeName || '',
    from_tour_index: params?.fromIndex ?? 0,
    to_tour_index: params?.toIndex ?? 0,
  });
};

/**
 * Fire when a user uses a pinch-zoom gesture inside the tour viewer.
 * @param {{ direction?: 'in'|'out', communeName?: string, tourTitle?: string }} params
 */
export const gaTrackMatterportPinchZoom = (params = {}) => {
  gtag('event', 'matterport_pinch_zoom', {
    event_category: 'Matterport Engagement',
    event_label: `pinch_${params?.direction || 'unknown'}_${params?.communeName || ''}`,
    gesture_type: 'pinch_zoom',
    zoom_direction: params?.direction || '',
    tour_commune: params?.communeName || '',
    tour_title: params?.tourTitle || '',
  });
};

/**
 * Fire when a user exits fullscreen mode.
 * @param {{ communeName?: string, tourTitle?: string, durationSeconds?: number }} params
 */
export const gaTrackMatterportFullscreenExit = (params = {}) => {
  gtag('event', 'matterport_fullscreen_exit', {
    event_category: 'Matterport Engagement',
    event_label: params?.tourTitle || params?.communeName || 'tour',
    tour_commune: params?.communeName || '',
    tour_title: params?.tourTitle || '',
    fullscreen_duration_seconds: params?.durationSeconds ?? 0,
  });
};

/**
 * Fire when a user switches connection quality (HD / SD / Éco).
 * @param {{ fromQuality?: string, toQuality?: string, communeName?: string, tourTitle?: string }} params
 */
export const gaTrackMatterportQualitySwitch = (params = {}) => {
  gtag('event', 'matterport_quality_switch', {
    event_category: 'Matterport Engagement',
    event_label: `${params?.fromQuality || '?'}_to_${params?.toQuality || '?'}`,
    from_quality: params?.fromQuality || '',
    to_quality: params?.toQuality || '',
    tour_commune: params?.communeName || '',
    tour_title: params?.tourTitle || '',
  });
};

/**
 * Fire when a tour iframe finishes loading (completion signal).
 * @param {{ communeName?: string, tourTitle?: string, quality?: string, loadDurationSeconds?: number }} params
 */
export const gaTrackMatterportTourLoaded = (params = {}) => {
  gtag('event', 'matterport_tour_loaded', {
    event_category: 'Matterport Engagement',
    event_label: params?.tourTitle || params?.communeName || 'tour',
    tour_commune: params?.communeName || '',
    tour_title: params?.tourTitle || '',
    connection_quality: params?.quality || 'medium',
    load_duration_seconds: params?.loadDurationSeconds ?? 0,
  });
};

// ─── KPS Creator Studio ───────────────────────────────────────────────────────

/**
 * Fire when a user clicks the KPS Creator Studio download button.
 * @param {{ version?: string, platform?: string, source?: string }} params
 */
export const gaTrackKPSDownload = (params = {}) => {
  gtag('event', 'kps_creator_download', {
    event_category: 'KPS Creator Studio',
    event_label: `KPS_Creator_Setup_${params?.version || '1.0.0'}.exe`,
    app_version: params?.version || '1.0.0',
    platform: params?.platform || 'Windows',
    download_source: params?.source || 'hero_cta',
    value: 1,
  });

  // Also fire GA4 recommended file_download event
  gtag('event', 'file_download', {
    file_name: `KPS_Creator_Setup_${params?.version || '1.0.0'}.exe`,
    file_extension: 'exe',
    link_text: 'Télécharger pour Windows',
    link_url: window?.location?.href,
  });
};

/**
 * Fire when a user views the KPS Creator Studio page.
 * @param {{ referrer?: string }} params
 */
export const gaTrackKPSPageView = (params = {}) => {
  gtag('event', 'kps_studio_page_view', {
    event_category: 'KPS Creator Studio',
    event_label: 'page_view',
    page_referrer: params?.referrer || document?.referrer || '',
  });
};

/**
 * Fire when a user scrolls to a specific section of the KPS Creator Studio page.
 * @param {{ section: string }} params
 */
export const gaTrackKPSSectionView = (section) => {
  gtag('event', 'kps_section_view', {
    event_category: 'KPS Creator Studio',
    event_label: section || 'unknown_section',
    section_name: section || '',
  });
};

export default {
  initGA,
  trackPageView,
  gaTrackPropertyView,
  gaTrackAlertSignup,
  gaTrackNotificationReceived,
  gaTrackAppointmentBooked,
  gaTrackFunnelDropOff,
  gaTrackPropertySearch,
  gaTrackVideoView,
  gaTrackROICalculation,
  gaTrackInquiryCreated,
  gaTrackMatterportTourStart,
  gaTrackMatterportSwipeGesture,
  gaTrackMatterportPinchZoom,
  gaTrackMatterportFullscreenExit,
  gaTrackMatterportQualitySwitch,
  gaTrackMatterportTourLoaded,
  gaTrackKPSDownload,
  gaTrackKPSPageView,
  gaTrackKPSSectionView,
};
